<template>
  <div>
    <app-filter
      :filter-list="table_filter"
      default-label-width="120px"
      @search="search"
    />
    <div class="text-right mb-20">
      <el-button size="mini" type="success" @click="onPull">同步</el-button>
    </div>
    <app-table
      ref="app-table"
      :table-key="table_key"
      :tablep="table_params"
      :page-size="15"
      :get-data="getCampaignList"
      :table-props="{ 'max-height': '450px' }"
      :check="true"
      check-key="id"
      :is-reserve-checked="true"
      :is-row-click="true"
      @select-tr="selectionChange"
      @hook:mounted="onTableMounted"
    />
  </div>
</template>

<script>
import { CHANNEL_INTERFACE_TYPE } from '@/enums';
import { transformSchema, hasOwnProperty } from '@/utils';
import { transformTreeKey } from '@/tools/form';
import { getCampaignListApi, pullCampaignListApi } from '@/api/campaign';
import { getSiteSetApi } from '@/api/tool/site-set';

export default {
  components: {
    'app-filter': () => import('@/components/app-filter'),
    'app-table': () => import('@/components/app-table'),
  },
  props: {
    initConfig: {
      type: Object,
      required: true,
    },
    tableSelection: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      table_filter: require('./model/table-filter.js')(this),
      table_key: require('./model/table-key.js')(this),
      table_params: {
        page: 1,
        limit: 15,
      },
      table_selection: [],
      enum_map: {
        delivery_platform_type: {},
        site_sets: {},
      },
    };
  },
  computed: {
    /**
     * 渠道接口
     * @returns {string}
     */
    channel_interface() {
      const { init_ops: { channel_interface }} = this.initConfig;
      return channel_interface;
    },
    /**
     * 广告主ID
     * @returns {number}
     */
    advertiser_id() {
      const { init_ops: { advertiser_ids }} = this.initConfig;
      return advertiser_ids[0];
    },
  },
  created() {
    this.init();
  },
  methods: {
    /**
     * 初始化
     * @returns {void}
     */
    init() {
      this.addEvent();
      this.setDeliveryPlatform();
      this.setSiteSet();
      this.setTableFilter();
    },
    /**
     * 添加监听事件
     * @returns {void}
     */
    addEvent() {
      const { type, campaign_name, campaign_id, site_sets, site_set_type } = this.table_filter;
      type.on = {
        change: () => {
          const is_name = type.value === 'campaign_name';
          campaign_name.hidden = !is_name;
          campaign_id.hidden = is_name;
        },
      };
      site_set_type.on = {
        change: () => {
          const is_auto_site = site_set_type.value === '1';
          site_sets.props.disabled = is_auto_site;
          if (is_auto_site) {
            site_sets.value = [];
          }
        },
      };
    },
    /**
     * 表格选择事件
     * @param {object} rows 表格数据
     * @returns {void}
     */
    selectionChange(rows) {
      const strategy = {
        [CHANNEL_INTERFACE_TYPE.GDT]: () => {
          let mes = '';
          const is_valid = rows.every(({ delivery_platform_type, site_set_type, site_sets, gdt_params }) => {
            const row = rows[0];
            const valid_list = [
              { result: delivery_platform_type === row.delivery_platform_type, message: '投放平台' },
              { result: gdt_params.marketing_sub_goal === row.gdt_params.marketing_sub_goal, message: '投放目的' },
              { result: delivery_platform_type === row.delivery_platform_type, message: '投放平台' },
              { result: site_set_type === row.site_set_type, message: '广告版位' },
              { result: this.isArraysEqual(site_sets, row.site_sets), message: '广告版位' },
            ];
            return valid_list.every(({ result, message }) => {
              !result && (mes = message);
              return result;
            });
          });

          return { is_valid, mes };
        },
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          let mes = '';
          const is_valid = rows.every(({ delivery_platform_type, site_set_type, site_sets, price_type, price_strategy, jrtt_params }) => {
            const row = rows[0];
            const valid_list = [
              { result: delivery_platform_type === row.delivery_platform_type, message: '投放平台' },
              { result: jrtt_params.app_promotion_type === row.jrtt_params.app_promotion_type, message: '投放目的' },
              { result: site_set_type === row.site_set_type, message: '广告版位' },
              { result: this.isArraysEqual(site_sets, row.site_sets), message: '广告版位' },
              { result: price_type === row.price_type, message: '出价方式' },
              { result: price_strategy === row.price_strategy, message: '出价策略' },
              { result: jrtt_params.marketing_goal === row.jrtt_params.marketing_goal, message: '营销场景' },
              { result: jrtt_params.delivery_mode === row.jrtt_params.delivery_mode, message: '投放方式' },
              { result: jrtt_params.star_task_switch === row.jrtt_params.star_task_switch, message: '关联星广联投' },
              { result: jrtt_params.star_auto_material_addition_switch === row.jrtt_params.star_auto_material_addition_switch, message: '自动优选素材' },
              { result: jrtt_params.convert_goal === row.jrtt_params.convert_goal, message: '优化目标' },
              { result: jrtt_params.deep_convert_goal === row.jrtt_params.deep_convert_goal, message: '深度优化目标' },
              { result: jrtt_params.deep_bid_type === row.jrtt_params.deep_bid_type, message: '深度优化方式' },
              { result: jrtt_params.budget_optimize_switch === row.jrtt_params.budget_optimize_switch, message: '预算择优分配' },
            ];
            return valid_list.every(({ result, message }) => {
              !result && (mes = message);
              return result;
            });
          });

          return { is_valid, mes };
        },
        [CHANNEL_INTERFACE_TYPE.BILIBILI]: () => {
          return { is_valid: rows.length <= 1, coverMsg: '仅支持选择一条广告' };
        },
        [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
          let mes = '';
          const reference_row = rows[0];
          const is_valid = rows.every((row) => {
            const valid_list = [
              { result: row.delivery_platform_type === reference_row.delivery_platform_type, message: '投放平台' },
              { result: row.site_set_type === reference_row.site_set_type, message: '广告版位' },
              { result: this.isArraysEqual(row.site_sets, reference_row.site_sets), message: '广告版位' },
              { result: hasOwnProperty(row, 'price') === hasOwnProperty(reference_row, 'price'), message: '广告出价' },
              { result: hasOwnProperty(row.baidu_params, 'ocpc_bid') === hasOwnProperty(reference_row.baidu_params, 'ocpc_bid'), message: '优化目标出价' },
              { result: hasOwnProperty(row.baidu_params, 'deep_ocpc_bid') === hasOwnProperty(reference_row.baidu_params, 'deep_ocpc_bid'), message: '深度优化目标出价' },
              { result: hasOwnProperty(row.baidu_params, 'roi_ratio') === hasOwnProperty(reference_row.baidu_params, 'roi_ratio'), message: '深度ROI系数出价' },
            ];
            return valid_list.every(({ result, message }) => {
              !result && (mes = message);
              return result;
            });
          });

          return { is_valid, mes };
        },
      };

      const { is_valid, mes, coverMsg } = strategy[this.channel_interface]();

      if (!is_valid) {
        this.$message.warning(coverMsg || `所选广告中的${mes}配置不一致`);
        this.resetTableSelection();
        return;
      }
      this.table_selection.splice(0);
      this.table_selection.push(...rows);
    },
    /**
     * 获取表格已选项
     * @returns {Object[]}
     */
    getTableSelection() {
      return this.table_selection;
    },
    /**
     * 初始化表格选中数据
     * @returns {void}
     */
    initRowsSelection() {
      this.table_selection.splice(0);
      this.table_selection.push(...this.tableSelection);

      this.resetTableSelection();
    },
    /**
     * 重置表格选中
     * @returns {void}
     */
    resetTableSelection() {
      const app_table_ref = this.$refs['app-table'];
      app_table_ref?.$refs?.table?.clearSelection?.();

      app_table_ref?.multipleSelection?.splice(0);
      app_table_ref?.multipleSelection?.push(...this.table_selection);
      app_table_ref?.multipleSelectionAll?.splice(0);
      app_table_ref?.multipleSelectionAll?.push(...this.table_selection);

      app_table_ref?.selectedPreExisting?.(this.table_selection);
    },
    /**
     * 监听表格挂载完成
     * @returns {void}
     */
    onTableMounted() {
      this.initRowsSelection();
    },
    /**
     * 搜索
     * @param params 表格参数
     * @returns {void}
     */
    search(params) {
      const { site_set_type, ...rest } = params;
      const { page, limit } = this.table_params;
      const payload = {
        ...(site_set_type === '1' && { site_set_type }),
        ...rest,
        page,
        limit,
      };

      Object.keys(this.table_params).forEach(key => {
        delete this.table_params[key];
      });
      Object.assign(this.table_params, payload);
      this.$refs['app-table'].seekTable();
    },
    /**
     * 获取广告列表
     * @param {Object} params 接口参数
     * @returns {Promise}
     */
    getCampaignList(params) {
      return new Promise((resolve, reject) => {
        const { page, limit, ...rest } = params;
        getCampaignListApi({
          filtering: {
            ...rest,
          },
          channel_interface: this.channel_interface,
          advertiser_id: this.advertiser_id,
          page,
          limit,
        }).then(resolve).catch(reject);
      });
    },
    /**
     * 设置版位
     * @returns {Promise<void>}
     */
    async setSiteSet() {
      const { list } = await getSiteSetApi({ channel_interface: this.channel_interface, advertiser_id: this.advertiser_id });
      const result = transformTreeKey({ options: list, label_key: 'name', value_key: 'id' });

      const strategy = {
        [CHANNEL_INTERFACE_TYPE.GDT]: () => {
          return result.some(({ value }) => value === 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN')
            ? [...result.filter(({ value }) => value !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), { label: '微信公众号与小程序', value: 'SITE_SET_WECHAT' }, { label: '微信新闻插件', value: 'SITE_SET_WECHAT_PLUGIN' }]
            : result;
        },
        default: () => {
          return result;
        },
      };
      const options = (strategy[this.channel_interface] || strategy['default'])();
      this.table_filter.site_sets.options = options;
      this.enum_map.site_sets = transformSchema(options);
    },
    /**
     * 设置投放平台
     * @returns {void}
     */
    setDeliveryPlatform() {
      this.enum_map.delivery_platform_type = this.$store.getters[`${this.channel_interface}EnumVal`].api_fusion_delivery_platform_type;
    },
    /**
     * 两个数组值是否相等
     * @param arr1 数组1
     * @param arr2 数组2
     * @returns {boolean}
     */
    isArraysEqual(arr1 = [], arr2 = []) {
      if (arr1.length !== arr2.length) {
        return false;
      }
      const a1 = JSON.parse(JSON.stringify(arr1));
      const a2 = JSON.parse(JSON.stringify(arr2));

      return a1.sort().toString() === a2.sort().toString();
    },
    /**
     * 同步列表
     * @returns {void}
     */
    onPull() {
      pullCampaignListApi({
        channel_interface: this.channel_interface,
        advertiser_id: this.advertiser_id,
      }).then(({ message }) => {
        this.$message.success(message || '同步成功');
        this.$refs['app-table'].seekTable();
      });
    },
    /**
     * 设置过滤项表单
     * @returns {void}
     */
    setTableFilter() {
      const { site_sets, site_set_type } = this.table_filter;
      const is_hidden_site_sets = [CHANNEL_INTERFACE_TYPE.BILIBILI].includes(this.channel_interface);
      site_sets.hidden = is_hidden_site_sets;
      site_set_type.hidden = is_hidden_site_sets;
    },
  },
};
</script>
